import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createPublicClient, http, type PublicClient } from 'viem';
import { robinhoodTestnet } from '../../shared/chains';
import { readMarket, readUser, type UserSnapshot } from '../../shared/reader';
import type { Address, EthFaucetInfo, MarketSnapshot, PriceHistory, StatusResponse } from '../../shared/types';

export const publicClient = createPublicClient({ chain: robinhoodTestnet, transport: http(undefined, { timeout: 15_000, retryCount: 2 }) }) as PublicClient;

export async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`${path} -> ${res.status}`);
  return (await res.json()) as T;
}

/**
 * Market-wide data comes from the edge snapshot (one chain read shared by every visitor). If the API is down the
 * page degrades to reading the chain directly, so the dapp never depends on the Worker being healthy.
 */
async function loadMarket(fresh: boolean): Promise<MarketSnapshot> {
  try {
    return await getJson<MarketSnapshot>(fresh ? `/api/market?fresh=${Date.now()}` : '/api/market');
  } catch {
    return readMarket(publicClient, 'browser');
  }
}

export const keys = {
  market: ['market'] as const,
  user: (a: Address | null) => ['user', a] as const,
  prices: (range: string) => ['prices', range] as const,
  status: ['status'] as const,
  ethFaucet: (a: Address | null) => ['eth-faucet', a] as const,
};

export const useMarket = () => useQuery({ queryKey: keys.market, queryFn: () => loadMarket(false), refetchInterval: 20_000, staleTime: 10_000 });

export const useUser = (address: Address | null) =>
  useQuery<UserSnapshot>({ queryKey: keys.user(address), queryFn: () => readUser(publicClient, address!), enabled: !!address, refetchInterval: 20_000, staleTime: 5_000 });

export const usePrices = (range: '1d' | '7d' | '30d') =>
  useQuery({ queryKey: keys.prices(range), queryFn: () => getJson<PriceHistory>(`/api/prices?range=${range}`), refetchInterval: 120_000, staleTime: 60_000, retry: 1 });

/** The ETH gas faucet's rules and, for a connected wallet, when it may claim. */
export const useEthFaucet = (address: Address | null) =>
  useQuery<EthFaucetInfo>({ queryKey: keys.ethFaucet(address), queryFn: () => getJson<EthFaucetInfo>(`/api/faucet/eth${address ? `?address=${address}` : ''}`), refetchInterval: 30_000, staleTime: 10_000, retry: 1 });

export const useStatus = () => useQuery({ queryKey: keys.status, queryFn: () => getJson<StatusResponse>('/api/status'), refetchInterval: 30_000, retry: 1 });

/** After a confirmed transaction: the user's numbers come straight from the chain, the market snapshot is forced fresh. */
export function useRefreshAfterTx() {
  const qc = useQueryClient();
  return async (address: Address | null) => {
    await Promise.all([
      qc.fetchQuery({ queryKey: keys.market, queryFn: () => loadMarket(true), staleTime: 0 }).catch(() => undefined),
      address ? qc.invalidateQueries({ queryKey: keys.user(address) }) : Promise.resolve(),
      address ? qc.invalidateQueries({ queryKey: ['history', address] }) : Promise.resolve(),
    ]);
  };
}
